import { useMemo, useCallback } from 'react';
import { ReactComponent as ArrowIcon } from '../../../../assets/icons/arrow-down.svg';
import Button from '../../../BaseComponents/Button';
import Select from '../../../BaseComponents/Select';
import TextInput from '../../../BaseComponents/TextInput';
import { useFormikContext } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  changeDeleteSingleBiomarkerResultModalVisibility,
  setSelectedTestResultIdToDelete
} from '../../../../store/alertsSlice';
import { biomarkerMeasurementUnits } from '../../../../utils/constants';
import { filterActiveBiomarkers } from '../../../../utils/biomarkers';

// Constants
const BIOMARKER_TYPES = {
  BINARY: 'BINARY',
  RANGES: 'RANGES',
  UNITS: 'UNITS'
} as const;

const BINARY_VALUES = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEGATIVE'
} as const;

// Types
interface BiomarkerOption {
  key: string;
  value: string;
  type: string;
  description: string;
}

interface BiomarkerFormData {
  id: string;
  name: { key: string; value: string; type?: string };
  value: string;
  summary: string;
  measurement_unit?: string;
  biomarker_result_id?: string;
}

interface SingleBiomarkerTestResultProps {
  biomarkerIndex: number;
  biomarkerData: BiomarkerFormData;
  openedAccordion: string | null;
  setOpenedBiomarker: (biomarkerId: string | null) => void;
  mode: 'create' | 'update';
}

const SingleBiomarkerTestResult = ({
  biomarkerIndex,
  biomarkerData,
  openedAccordion,
  setOpenedBiomarker,
  mode
}: SingleBiomarkerTestResultProps) => {
  const dispatch = useDispatch();
  const { setFieldValue, values } = useFormikContext<any>();
  const { biomarkersList } = useSelector((state: RootState) => state.app);
  const { memberBiomarkersExistingIdsByAppointment } = useSelector(
    (state: RootState) => state.member
  );

  // Memoized handlers
  const onOpenBiomarker = useCallback(() => {
    setOpenedBiomarker(
      biomarkerData.id === openedAccordion ? null : biomarkerData.id
    );
  }, [biomarkerData.id, openedAccordion, setOpenedBiomarker]);

  const onDeleteBiomarker = useCallback(() => {
    if (mode === 'create') {
      const updatedBiomarkers = values.biomarkers.filter(
        (biomarker: BiomarkerFormData) => biomarker.id !== biomarkerData.id
      );
      setFieldValue('biomarkers', updatedBiomarkers);
    } else {
      dispatch(changeDeleteSingleBiomarkerResultModalVisibility(true));
      dispatch(setSelectedTestResultIdToDelete(parseInt(biomarkerData.id)));
    }
  }, [mode, values.biomarkers, biomarkerData.id, setFieldValue, dispatch]);

  const activeBiomarkersList = useMemo(
    () => filterActiveBiomarkers(biomarkersList),
    [biomarkersList]
  );

  // Memoized options
  const biomarkersOptions = useMemo((): BiomarkerOption[] => {
    return activeBiomarkersList.map((biomarker: any) => ({
      key: biomarker.id,
      value: biomarker.name,
      type: biomarker.biomarker_type,
      description: biomarker.description
    }));
  }, [activeBiomarkersList]);

  const onBiomarkerNameChange = useCallback((option: any, name: string) => {
    // Find the full biomarker option with type information
    const fullOption = biomarkersOptions.find(opt => opt.key === option.key);
    if (fullOption) {
      // Find the complete biomarker data from biomarkersList
      const biomarkerData = activeBiomarkersList.find((b: any) => b.id === fullOption.key);
      
      setFieldValue(`biomarkers[${biomarkerIndex}].name`, {
        key: fullOption.key,
        value: fullOption.value,
        type: fullOption.type
      });
      setFieldValue(`biomarkers[${biomarkerIndex}].value`, '');
      
      // Prefill the unit from the selected biomarker
      if (biomarkerData?.measurement_unit) {
        setFieldValue(`biomarkers[${biomarkerIndex}].measurement_unit`, biomarkerData.measurement_unit);
      }
    }
  }, [setFieldValue, biomarkerIndex, biomarkersOptions, activeBiomarkersList]);

  const onValueChange = useCallback((value: string) => {
    setFieldValue(`biomarkers[${biomarkerIndex}].value`, value);
  }, [setFieldValue, biomarkerIndex]);

  const onSummaryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`biomarkers[${biomarkerIndex}].summary`, e.target.value);
  }, [setFieldValue, biomarkerIndex]);

  const availableBiomarkerOptions = useMemo(() => {
    return biomarkersOptions.filter((option) => {
      const isSelected = values.biomarkers?.some(
        (b: BiomarkerFormData) => b.name?.key === option.key
      );
      const isExisting = memberBiomarkersExistingIdsByAppointment?.includes(parseInt(option.key));
      return !isSelected && !isExisting;
    }).map(option => ({
      key: option.key,
      value: option.value
    }));
  }, [biomarkersOptions, values.biomarkers, memberBiomarkersExistingIdsByAppointment]);

  // Current biomarker data
  const currentBiomarker = values.biomarkers[biomarkerIndex];
  const isOpen = biomarkerData.id === openedAccordion;
  const isBinaryType = currentBiomarker?.name?.type === BIOMARKER_TYPES.BINARY;

  return (
    <div
      className={`single-biomarker-test-result ${
        isOpen ? 'opened-single-biomarker-test-result' : ''
      }`}
    >
      <div
        className='single-biomarker-test-result-header'
        onClick={onOpenBiomarker}
      >
        <h3>
          {currentBiomarker?.name?.value || 'Biomarker'}
        </h3>
        <div className='value-and-arrow-container'>
          {!isOpen && (
            <p className='value-and-arrow-container-value'>
              {currentBiomarker?.value}{' '}
              {!(currentBiomarker?.value?.toLowerCase() === BINARY_VALUES.POSITIVE.toLowerCase() || 
              currentBiomarker?.value?.toLowerCase() === BINARY_VALUES.NEGATIVE.toLowerCase())
              && currentBiomarker?.measurement_unit
              ? ` ${biomarkerMeasurementUnits.find(unit => unit.key === currentBiomarker.measurement_unit)?.value || currentBiomarker.measurement_unit}`
              : ''}
            </p>
          )}
          <ArrowIcon
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }}
          />
        </div>
      </div>
      
      <div className='single-biomarker-test-result-content'>
        <Select
          name='biomarker_name'
          title='Biomarker name *'
          placeholder='Select biomarker'
          options={availableBiomarkerOptions}
          selectedOption={currentBiomarker?.name ? {
            key: currentBiomarker.name.key,
            value: currentBiomarker.name.value
          } : undefined}
          onChange={onBiomarkerNameChange}
          isError={false}
        />
        
        {currentBiomarker?.name?.key && (
          <>
            {isBinaryType ? (
              <div>
                <div className='section-title'>Value *</div>
                <div className='inputs-wrapper'>
                  <div className='radio-group d-flex flex-column gap-10'>
                    <div>
                      <label className='d-flex align-items-center gap-10'>
                        <input
                          type='radio'
                          name={`biomarker_${biomarkerIndex}_value`}
                          checked={currentBiomarker.value === BINARY_VALUES.NEGATIVE}
                          onChange={() => onValueChange(BINARY_VALUES.NEGATIVE)}
                        />
                        Negative
                      </label>
                    </div>
                    <div>
                      <label className='d-flex align-items-center gap-10'>
                        <input
                          type='radio'
                          name={`biomarker_${biomarkerIndex}_value`}
                          checked={currentBiomarker.value === BINARY_VALUES.POSITIVE}
                          onChange={() => onValueChange(BINARY_VALUES.POSITIVE)}
                        />
                        Positive
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <TextInput
                  name='biomarker_value'
                  title='Value *'
                  placeholder='Enter biomarker value'
                  value={currentBiomarker.value}
                  onChange={(e) => onValueChange(e.target.value)}
                  isError={false}
                />
                <TextInput
                  name='biomarker_unit'
                  title='Unit'
                  placeholder='Unit will be filled automatically'
                  value={currentBiomarker?.measurement_unit ? 
                    biomarkerMeasurementUnits.find(unit => unit.key === currentBiomarker.measurement_unit)?.value || currentBiomarker.measurement_unit
                    : ''
                  }
                  onChange={() => {}} // Read-only field
                  isError={false}
                  isDisabled={true}
                />
              </>
            )}
          </>
        )}
        
        <TextInput
          name='biomarker_summary'
          title='Summary'
          placeholder='Enter summary'
          onChange={onSummaryChange}
          value={currentBiomarker?.summary || ''}
          isError={false}
        />
        
        <Button
          variant='delete'
          text='Delete Biomarker'
          onClick={onDeleteBiomarker}
          backgroundColor='#ffffff'
          color='red'
        />
      </div>
    </div>
  );
};

export default SingleBiomarkerTestResult;
